import { map } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'
import { MediaVideoCreateModal } from './MediaVideoCreateModal'
import Loader from '../../../common/components/Loader'
import { Video } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  deleteVideo,
  getVideos,
  publishVideo,
  unpublishVideo,
} from '../../../core/services/video'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { Icon } from '@/common/components/Icon'
import { ActionsMenu } from '@/components/ActionsMenu'

export const MediaVideoPage: React.FC = (): JSX.Element => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()
  const { hasEditPermission } = useCheckPermission({ page: 'VIDEO_GALLERY' })
  const [videos, setVideos] = useState<Video[]>()
  const [currentVideo, setCurrentVideo] = useState<Video>()
  const [showVideCreateModal, setShowVideoCreateModal] =
    useState<boolean>(false)

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [editMode, setEditMode] = useState<boolean>(false)
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)
  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const editionId = storeFestivals
    .find((f) => f.platformName === festivalName)
    ?.editions.find((e) => e.active)?.id
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const response = await getVideos(editionId as string, {
        page,
        size,
        sortBy,
        sortDirection,
      })
      setVideos(response.data.content)
      setTotalElemenets(response.data.totalElements)
      setSize(response.data.size)
      setPage(response.data.pageNumber + 1)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    page,
    size,
    sortBy,
    editionId,
    festivalName,
    sortDirection,
    setPage,
    setSize,
    setTotalElemenets,
  ])

  useEffect(() => {
    getData()
  }, [getData])

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const handleSelectAll = () => {
    if (!hasEditPermission) {
      return
    }
    setIsCheckAll(!isCheckAll)
    setIsCheck(map(videos, (video) => video.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    if (!hasEditPermission) {
      return
    }
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handlePublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        setIsCheck([])
        const { status } = await publishVideo(editionId as string, isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setIsCheck([])
          setMessage('Publish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const handleUnpublish = (isCheck: string[]) => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        setIsCheck([])
        const { status } = await unpublishVideo(editionId as string, isCheck)
        getData()
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Unpublish Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        const { status } = await deleteVideo(editionId as string, isCheck[0])
        getData()
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={handleSelectAll}
          checked={isCheckAll}
        />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          disabled={!hasEditPermission}
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),

      width: '16px',
    },
    {
      title: 'Preview',
      accessor: '',
      render: (row) => (
        <img
          src={
            row.previewImageLink !== '' ? row.previewImageLink : row.videoLink
          }
          className="rounded-[10px] w-[108px] h-[70px] object-contain"
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: false,
    },
    {
      title: 'Publish Date',
      accessor: 'publishedAt',
      sortable: true,
      render: (row) => sanitizeDate(new Date(row.publishedAt)),
    },
    {
      title: 'Published',
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={() => {
            if (row.published) {
              handleUnpublish([row.id])
            } else {
              handlePublish([row.id])
            }
          }}
          checked={row.published}
          disabled={!hasEditPermission}
        />
      ),
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row) => (
        <a
          className="view text-accent-1 underline"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            if (!hasEditPermission) {
              return
            }
            setCurrentVideo(row)
            setEditMode(true)
            setShowVideoCreateModal(true)
          }}
        >
          Edit
        </a>
      ),
    },
  ]

  return (
    <>
      <Header
        pageTitle="Media"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <>
                <ActionsMenu
                  actions={[
                    { label: 'Publish', onClick: () => handlePublish(isCheck) },
                    {
                      label: 'Unpublish',
                      onClick: () => handleUnpublish(isCheck),
                    },
                  ]}
                />
                <Button variant="destructive" onClick={handleDelete}>
                  Delete
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setShowVideoCreateModal(true)
                  setCurrentVideo(undefined)
                  setEditMode(false)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Media" subtitle="Video Gallery" className="mb-5" />
        <Table
          maxHeight="calc(100svh - 300px)"
          isCheck={isCheck}
          columns={columns}
          handleSortBy={handleSortBy}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data={videos || []}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <MediaVideoCreateModal
        showCreateModal={showVideCreateModal}
        setShowCreateModal={setShowVideoCreateModal}
        setEditMode={setEditMode}
        editMode={editMode}
        page={page + 1}
        size={size}
        setTotalElemenets={setTotalElemenets}
        setVideos={setVideos}
        video={currentVideo}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Video'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
